import React, {useEffect, useState, useRef} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';

import {labels} from '../../../../../util/utils';
import Utility from '../../../../../util/utilityfunctions';
import {getPercent} from '@ey/utils';
import {loadStorage, saveStorage} from '../../../../Common/localStorage';
import {projectSessionKeys} from '../../../../../util/uiconstants';
import {assignmentsCustomizeIds} from '../../../../Common/MetricsTable/columns';

import BadgeLabel from '../../../../Common/Badge/BadgeLabel';
import ExportToExcel from '../../../../Common/ExportToExcel/ExportToExcel';
import FilterChips from '../../../../Common/FilterChips';
import Select from '../../../../CoreWrappers/Select';
import MetricsTable from '../../../../Common/MetricsTable';

const ProjectManagementEngagementTable = ({engagementMetrics}) => {
	const numberFormat = useSelector(({userPreferences}) => userPreferences.numberFormat);
	const dashboardContext = useSelector(({dashboardContext}) => dashboardContext);
	const dashboardProjectManagementFilters = useSelector(({dashboardFilters}) => dashboardFilters.projectManagement);
	const hasFiltersApplyed = dashboardProjectManagementFilters?.filters?.length || false;
	const chipFilters = dashboardProjectManagementFilters?.filters;

	const filterId = {
		assignments: 1,
		notes: 2
	};
	const filters = [
		{id: filterId.assignments, label: labels.assignments},
		{id: filterId.notes, label: labels.notes}
	];

	const maxColumnConfiguarable = 4;

	const assignmentsCustomizeOptions = [
		{id: 'preparerHeader', label: labels.preparerAssignments, isHeader: true},
		{id: assignmentsCustomizeIds.preparerAssignmentsId, label: labels.preparerAssignments},
		{id: assignmentsCustomizeIds.preparerAssignmentsStatusId, label: labels.status},
		{id: assignmentsCustomizeIds.assignmentsOverdueId, label: labels.overdueLabel},
		{id: assignmentsCustomizeIds.preparerNotAssignedId, label: labels.preparerNotAssigned},
		{id: 'reviewerHeader', label: labels.reviewerAssignments, isHeader: true},
		{id: assignmentsCustomizeIds.reviewerAssignmentsId, label: labels.reviewerAssignments},
		{id: assignmentsCustomizeIds.ReviewerAssignmentsStatusId, label: labels.status},
		{id: assignmentsCustomizeIds.ReviewerAssignmentsOverdueId, label: labels.overdueLabel},
		{id: assignmentsCustomizeIds.ReviewerNotAssignedId, label: labels.reviewerNotAssigned}
	];

	const assignmentsCustomizeInitial = [
		assignmentsCustomizeIds.preparerAssignmentsId,
		assignmentsCustomizeIds.preparerAssignmentsStatusId,
		assignmentsCustomizeIds.reviewerAssignmentsId,
		assignmentsCustomizeIds.ReviewerAssignmentsStatusId
	];

	const [selectedFilter, setSelectedFilter] = useState(filterId.assignments);
	const [engagementData, setEngagementData] = useState(null);
	const [showMessage, setShowMessage] = useState(false);
	const [userChange, setUserChange] = useState(false);
	const openGroups = useRef([]);
	const projectId = Utility.getProjectIdFromUrl();
	const psessionKEy = `${projectSessionKeys.projectID}_${projectId}_${projectSessionKeys.projectManagementSession}`;

	useEffect(() => {
		const currentState = loadStorage(psessionKEy);
		if (currentState && currentState.projectManagementFilterid) {
			setSelectedFilter(currentState.projectManagementFilterid);
		}
	}, []);

	useEffect(() => {
		buildTable();
	}, [engagementMetrics, selectedFilter, chipFilters]);

	useEffect(() => {
		if (hasFiltersApplyed) {
			const isAssignmentGraphicFilterApplied = chipFilters?.some((chip) =>
				[labels.preparerAssignments, labels.reviewerAssignments].includes(chip.categoryLabel)
			);
			const selectedTable = isAssignmentGraphicFilterApplied ? filterId.assignments : filterId.notes;
			setSelectedFilter(selectedTable);
			let currentState = loadStorage(psessionKEy) || {};
			saveStorage(psessionKEy, {...currentState, projectManagementFilterid: selectedTable});
		}
	}, [dashboardProjectManagementFilters]);

	const getExportDetails = () => {
		return {
			title: filters.find((f) => f.id === selectedFilter).label,
			columns: getColumnsToExport(),
			data: getDataToExport(),
			worksheetName: filters.find((f) => f.id === selectedFilter).label
		};
	};

	const getDataToExport = () => {
		if (isAssignments) {
			return getAssignmentsExcelData();
		} else {
			return getNotesExcelData();
		}
	};

	const getColumnsToExport = () => {
		let columns = [];
		columns.push({header: labels.orgChartEntity, key: 'entityName'});
		columns.push({header: labels.engagementId, key: 'engId'});
		columns.push({header: labels.contactName, key: 'ContactName'});
		columns.push({header: labels.contactEmail, key: 'ContactEmail'});
		columns.push({header: labels.engagementLabel, key: 'engDes'});
		if (isAssignments) {
			columns.push({header: labels.preparerAssignments, key: 'preparerAssignments'});
			columns.push({header: labels.statusOpen, key: 'statusOpenPreparer'});
			columns.push({header: labels.statusInProgress, key: 'statusInProgressPreparer'});
			columns.push({header: labels.statusClosed, key: 'statusClosedPreparer'});
			columns.push({header: labels.overdueLabel, key: 'overduePreparer'});
			columns.push({header: labels.preparerNotAssigned, key: 'notAssignedPreparer'});
			columns.push({header: labels.reviewerAssignments, key: 'reviewerAssignments'});
			columns.push({header: labels.statusOpen, key: 'statusOpenReviewer'});
			columns.push({header: labels.statusInProgress, key: 'statusInProgressReviewer'});
			columns.push({header: labels.statusClosed, key: 'statusClosedReviewer'});
			columns.push({header: labels.overdueLabel, key: 'overdueReviewer'});
			columns.push({header: labels.reviewerNotAssigned, key: 'notAssignedReviewer'});
		} else {
			columns.push({header: labels.totalNotesLabel, key: 'totalNotes'});
			columns.push({header: labels.openNotes, key: 'openNotes'});
			columns.push({header: labels.overdueLabel, key: 'overdueNotes'});
			columns.push({header: labels.byPriorityLow, key: 'priorityLow'});
			columns.push({header: labels.byPriotityMedium, key: 'priorityMedium'});
			columns.push({header: labels.byPriorityHigh, key: 'priorityHigh'});
			columns.push({header: labels.byPriorityCritical, key: 'priorityCritical'});
		}
		return columns;
	};

	const getAssignmentsExcelData = () => {
		let data = [];
		dashboardContext.orgChart.forEach((ochart) => {
			if (dashboardContext && _.size(dashboardContext.entities) > 0) {
				dashboardContext.entities.forEach((entity) => {
					if (entity.engagements && _.size(entity.engagements) > 0) {
						entity.engagements.forEach((eng) => {
							let engMetricsData = engagementMetrics?.find((x) => x.engId === eng);
							if (engMetricsData) {
								data.push({
									entityName: entity.label,
									engId: eng,
									ContactName: '',
									ContactEmail: '',
									engDes: engMetricsData.engDes,
									preparerAssignments: engMetricsData.pATot,
									statusOpenPreparer: getNumberWithPercentage(engMetricsData.pAOpen, engMetricsData.pATot),
									statusInProgressPreparer: getNumberWithPercentage(engMetricsData.pAInProg, engMetricsData.pATot),
									statusClosedPreparer: getNumberWithPercentage(engMetricsData.pACompld, engMetricsData.pATot),
									overduePreparer: getNumberWithPercentage(
										engMetricsData.pAOpenOD + engMetricsData.pAInProgOD,
										engMetricsData.pATot
									),
									notAssignedPreparer: engMetricsData.pAUnAsgn,
									reviewerAssignments: engMetricsData.rA,
									statusOpenReviewer: getNumberWithPercentage(engMetricsData.rANotStart, engMetricsData.rA),
									statusInProgressReviewer: getNumberWithPercentage(engMetricsData.rAInProg, engMetricsData.rA),
									statusClosedReviewer: getNumberWithPercentage(engMetricsData.rACompld, engMetricsData.rA),
									overdueReviewer: getNumberWithPercentage(
										engMetricsData.rANotStartOD + engMetricsData.rAInProgOD,
										engMetricsData.rA
									),
									notAssignedReviewer: engMetricsData.rAUnAsgn
								});
							}
						});
					}
				});
			}
		});
		return data;
	};

	const getNotesExcelData = () => {
		let data = [];
		dashboardContext.orgChart.forEach((ochart) => {
			if (dashboardContext && _.size(dashboardContext.entities) > 0) {
				dashboardContext.entities.forEach((entity) => {
					if (entity.engagements && _.size(entity.engagements) > 0) {
						entity.engagements.forEach((eng) => {
							let engMetricsData = engagementMetrics?.find((x) => x.engId === eng);
							if (engMetricsData) {
								data.push({
									entityName: entity.label,
									engId: eng,
									ContactName: '',
									ContactEmail: '',
									engDes: engMetricsData.engDes,
									totalNotes:
										(engMetricsData.rNO || 0) +
										(engMetricsData.rNCLow || 0) +
										(engMetricsData.rNCMed || 0) +
										(engMetricsData.rNCHigh || 0) +
										(engMetricsData.rNCCrit || 0) +
										(engMetricsData.rNCloMed || 0) +
										(engMetricsData.rNCloLow || 0) +
										(engMetricsData.rNCloCrit || 0) +
										(engMetricsData.rNCloHigh || 0),
									totalNotes: engMetricsData.rNTotal,
									openNotes: engMetricsData.rNO,
									overdueNotes: getNumberWithPercentage(
										engMetricsData.rNOLowOD +
											engMetricsData.rNOMedOD +
											engMetricsData.rNOHighOD +
											engMetricsData.rNOCritOD,
										engMetricsData.rNO
									),
									priorityLow: getNumberWithPercentage(engMetricsData.rNOLow, engMetricsData.rNO),
									priorityMedium: getNumberWithPercentage(engMetricsData.rNOMed, engMetricsData.rNO),
									priorityHigh: getNumberWithPercentage(engMetricsData.rNOHigh, engMetricsData.rNO),
									priorityCritical: getNumberWithPercentage(engMetricsData.rNOCrit, engMetricsData.rNO)
								});
							}
						});
					}
				});
			}
		});
		return data;
	};

	const getNumberWithPercentage = (partialValue, total) => {
		return !total || !partialValue
			? '0 (0%)'
			: `${partialValue} (${Utility.getFormattedNumber(numberFormat, getPercent(partialValue, total))}%)`;
	};

	const barStyling = {
		// chartWidth: 180,
		// chartHeight: barItems1.length * 66,
		styleName: 'proj-mgt-table-chart',
		chartWrapper: 'proj-mgt-table-chart-wrap'
	};

	// Show 0 if NaN
	const showZeroifNaN = (value) => {
		if (isNaN(value)) {
			return 0;
		} else return value;
	};

	const getChartData = (chartElements, total) => {
		let barItems = [];
		chartElements.forEach((item, index) => {
			let percentage = getPercent(item.data, total);
			item.percentage = percentage;
			barItems.push({
				count: item.data,
				label: item.name,
				statusClass: '',
				color: item.color,
				toolTip: `${item.name} | ${item.data} (${percentage}%)`
			});
		});

		return {
			barStyling: barStyling,
			headerText: '',
			barMaxValue: total,
			showBarTitleTip: true,
			barItems: barItems,
			showCustomLegend: false,
			totalCount: total
		};
	};

	// Construct engagement table data from entities
	const buildTable = () => {
		// check if selected entities have engagements
		if (dashboardContext.entities?.every((entity) => !entity.engagements?.length)) {
			setShowMessage(true);
		} else {
			let data = [];
			// show entities base on org chart order and if it was selected
			dashboardContext.orgChart.forEach((node) => {
				loadNodes(node, dashboardContext.entities, data);
			});
			setShowMessage(false);
			// when searching, remove entity without engagement
			if (hasFiltersApplyed) {
				data = data?.filter((x) => x?.items?.length !== 0);
			}
			setEngagementData(data);
		}
	};

	// Load data for selected entity nodes
	const loadNodes = (node, entities, data) => {
		// check if entity is selected
		const entity = entities.find((x) => x.id === node.id);

		if (entity?.isEnabled && entity.engagements?.length > 0) {
			const engagements = getEngagements(entity);

			const _isGroupOpened = engagements?.length > 0 || isGroupOpened(entity.id);

			if (!_.isEmpty(engagements)) {
				const group = {
					id: entity.id,
					name: entity.label,
					isOpen: _isGroupOpened,
					items: engagements,
					engCount: engagements.length,
					customGroupRender: () => {
						const result = <BadgeLabel label={entity.label} value={group.engCount} />;
						return result;
					}
				};

				data.push(group);
			}
		}

		// load children entities for each node
		node.nodes.forEach((n) => {
			loadNodes(n, entities, data);
		});
	};

	// Get engagements for given entity
	const getEngagements = (entity) => {
		let engagements = entity.engagements.map((engagementId) => {
			return getEngagement(engagementId);
		});

		let areEngagements = engagements.filter((e) => e !== undefined);

		if (hasFiltersApplyed) {
			areEngagements = areEngagements.filter((eng) =>
				dashboardProjectManagementFilters?.engagements.includes(eng.engId)
			);
		}
		if (_.isEmpty(areEngagements)) {
			return [];
		} else {
			return areEngagements;
		}
	};

	const isGroupOpened = (id) => {
		return openGroups.current.some((x) => x === id);
	};

	// Get engagements details for given engagementId
	const getEngagement = (id) => {
		const item = engagementMetrics?.find((x) => x.engId === id);
		// Add some new fields to item as we need to do sorting on those values
		return item
			? {
					...item,
					pATOD: item.pAOpenOD + item.pAInProgOD,
					rATOD: item.rANotStartOD + item.rAInProgOD,
					rNOOD: item.rNOLowOD + item.rNOMedOD + item.rNOHighOD + item.rNOCritOD
			  }
			: item;
	};

	// Show data for the selected toogle (Assignment/Notes)
	const handlerOnSwitchChange = (id) => {
		setSelectedFilter(id);
		buildTable();

		let currentState = loadStorage(psessionKEy) || {};
		saveStorage(psessionKEy, {...currentState, projectManagementFilterid: id});

		dashboardProjectManagementFilters?.onClearFilter();
	};

	//TODO: refresh data if the accordion is open.
	const handlerOnGroupChange = (group, isOpen) => {
		if (isOpen) {
			openGroups.current.push(group.id);
		} else {
			openGroups.current = openGroups.current.filter((x) => x !== group.id);
		}
	};

	const isAssignments = selectedFilter === filterId.assignments;

	const renderCustomDetails = () => {
		if (hasFiltersApplyed) {
			const filterChipsProps = {
				filters: [...chipFilters],
				onClearFilter: dashboardProjectManagementFilters?.onClearFilter
			};

			return <FilterChips {...filterChipsProps} />;
		}
	};

	const renderTableSelect = () => {
		const props = {
			value: selectedFilter,
			wrapperClass: 'table-select',
			options: filters,
			onChange: handlerOnSwitchChange
		};

		return <Select {...props} />;
	};

	const renderTable = () => {
		const tableProps = {
			data: engagementData,
			variant: isAssignments ? 'assignments' : 'notes',
			renderHeader: (renderCustomizeColumns) => (
				<>
					{isAssignments && renderCustomizeColumns(labels.selectUpToFourOptionHeader)}
					{renderTableSelect()}
					<ExportToExcel getDataToExport={getExportDetails} className="export-data" />
				</>
			),
			renderFilter: renderCustomDetails
		};

		if (isAssignments) {
			tableProps.columnVisibilityOptions = assignmentsCustomizeOptions;
			tableProps.columnVisibility = assignmentsCustomizeInitial;
			tableProps.maxCustomColumns = maxColumnConfiguarable;
		}

		return <MetricsTable {...tableProps} />;
	};

	return renderTable();
};

ProjectManagementEngagementTable.defaultProps = {
	engagementMetrics: []
};

ProjectManagementEngagementTable.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			engId: PropTypes.number,
			engKey: PropTypes.number,
			rNTotal: PropTypes.number,
			rNO: PropTypes.number,
			rNOLow: PropTypes.number,
			rNOMed: PropTypes.number,
			rNOHigh: PropTypes.number,
			rNOCrit: PropTypes.number,
			rNOLowOD: PropTypes.number,
			rNOMedOD: PropTypes.number,
			rNOHighOD: PropTypes.number,
			rNOCritOD: PropTypes.number,
			pATot: PropTypes.number,
			pAOpen: PropTypes.number,
			pAOpenOD: PropTypes.number,
			pAInProg: PropTypes.number,
			pAInProgOD: PropTypes.number,
			pACompld: PropTypes.number,
			pAUnAsgn: PropTypes.number,
			rA: PropTypes.number,
			rANotStart: PropTypes.number,
			rANotStartOD: PropTypes.number,
			rAInProg: PropTypes.number,
			rAInProgOD: PropTypes.number,
			rACompld: PropTypes.number,
			rAUnAsgn: PropTypes.number
		})
	)
};

export default ProjectManagementEngagementTable;
